"use client";

import { useRef } from "react";
import {
  CheckItem,
  SectionHead,
  SectionShell,
  useBandRowAlign,
  useWholeRowBlocks,
} from "@/components/sections/kit";
import {
  useEnterStagger,
} from "@/components/sections/kit/motion";
import { routePath } from "@/lib/routes";
import { MONITORED, RELIABILITY_CHECKS } from "./data";

/**
 * Reliability — what the public status page monitors, beside the
 * contractual-uptime copy. (The per-city p50 card was removed in the claims
 * sweep, 2026-09-27: no figure is quoted that the status page doesn't measure.) Bespoke to /solutions/enterprise; the
 * shared ReliabilitySection (live status board) stays on other pages.
 */
export default function ReliabilityLatencySection() {
  const rootRef = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);

  useWholeRowBlocks(rootRef);
  useBandRowAlign(rootRef);
  useEnterStagger(rootRef, "[data-check-row]", { y: 14 });


  return (
    <SectionShell id="reliability" className="scroll-mt-[var(--grid-cell,72px)] md:pt-px md:pb-px">
      <div ref={rootRef}>
        <SectionHead
          eyebrow="Reliability"
          title="Measured in public. Up in writing."
          lead="Live routing latency on a public status page, and a 99.99% uptime SLA that lives in the Enterprise agreement — not on a marketing page."
        />
        <div
          data-grid-block
          className="grid items-center gap-10 lg:grid-cols-[480px_minmax(0,1fr)] lg:gap-16"
        >
          {/* The monitor card — names only; the numbers live on /status. */}
          <div
            ref={cardRef}
            className="rounded-ref-lg border border-line bg-surface px-[26px] py-6 shadow-ref-lg"
          >
            <div className="flex items-baseline gap-2.5 border-b border-line pb-3.5">
              <span className="font-jetbrains text-[11px] font-semibold uppercase tracking-[0.12em]">
                monitored on /status
              </span>
              <a
                href={routePath("status")}
                className="ml-auto font-jetbrains text-[10.5px] text-dim hover:text-brand-600"
              >
                live numbers ↗
              </a>
            </div>
            <div className="flex flex-col gap-[13px] pt-4">
              {MONITORED.map((row, i) => (
                <div
                  key={row.name}
                  className="grid grid-cols-[112px_minmax(0,1fr)_64px] items-center gap-3.5 sm:grid-cols-[170px_minmax(0,1fr)_64px]"
                >
                  <span
                    className={`whitespace-nowrap text-[13px] ${i === 0 ? "font-bold" : "font-medium"}`}
                  >
                    {row.name}
                  </span>
                  <span aria-hidden="true" className="block border-b border-dashed border-line-2" />
                  <span className="justify-self-end font-jetbrains text-xs text-muted">
                    {row.note}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* The contractual claims. */}
          <div className="flex flex-col gap-3">
            {RELIABILITY_CHECKS.map((check) => (
              <div key={check} data-check-row>
                <CheckItem className="items-start text-[14.5px] leading-[1.55]">{check}</CheckItem>
              </div>
            ))}
          </div>
        </div>
      </div>
    </SectionShell>
  );
}
